'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';

// ─────────────────────────────────────────────────────────────
// Spawn Debug Data Bus — PhaserGame pushes frames here
// ─────────────────────────────────────────────────────────────
export interface SpawnDebugFrame {
  depthSpeed: number;          // units/frame (depth units per second)
  activeObstacles: number;
  activeRamps: number;
  // Closest obstacle that hasn't passed the player yet
  closestObstacleDepth: number | null;   // negative = behind spawn, 0..1 = approaching
  closestRampDepth: number | null;
  // Spawn recycle range
  spawnMinDepth: number;
  spawnMaxDepth: number;
  runTime: number;
  postSafeFrac: number;
}

const debugListeners: Array<(frame: SpawnDebugFrame) => void> = [];

export function pushSpawnDebug(frame: SpawnDebugFrame) {
  debugListeners.forEach(fn => fn(frame));
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

/** Convert a depth value (0 = player, 1 = horizon) to an approximate world distance in metres */
function depthToMetres(depth: number): number {
  // Perspective mapping: depth 0 = ~0 m, depth 1 = ~80 m (tuned to feel)
  return Math.max(0, (1 - depth) * 80);
}

/** How many seconds until an obstacle at `depth` reaches the player given current depthSpeed (per second) */
function timeToReach(depth: number, depthSpeedPerSec: number): number {
  if (depthSpeedPerSec <= 0) return Infinity;
  // depth travels from its current value to ~1.0 to pass the player
  const remaining = 1.0 - depth;
  return remaining / depthSpeedPerSec;
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────
interface SpawnDebugOverlayProps {
  visible: boolean;
  onToggle: () => void;
}

export default function SpawnDebugOverlay({ visible, onToggle }: SpawnDebugOverlayProps) {
  const [frame, setFrame] = useState<SpawnDebugFrame | null>(null);
  const [spawnEvents, setSpawnEvents] = useState<string[]>([]);
  const prevObsDepth = useRef<number | null>(null);
  const prevRampDepth = useRef<number | null>(null);

  const onFrame = useCallback((f: SpawnDebugFrame) => {
    setFrame(f);

    // Detect when closest obstacle just spawned (depth jumped from positive to negative)
    if (
      f.closestObstacleDepth !== null &&
      prevObsDepth.current !== null &&
      prevObsDepth.current > 0.5 &&
      f.closestObstacleDepth < 0
    ) {
      const spawnDist = depthToMetres(f.closestObstacleDepth);
      const ttr = timeToReach(f.closestObstacleDepth, f.depthSpeed);
      const ts = new Date();
      const label = `${ts.getSeconds().toString().padStart(2,'0')}.${Math.floor(ts.getMilliseconds()/10).toString().padStart(2,'0')} OBS spawned @ ${spawnDist.toFixed(0)}m — ${ttr.toFixed(1)}s to reach`;
      setSpawnEvents(prev => {
        const next = [label, ...prev];
        return next.length > 12 ? next.slice(0, 12) : next;
      });
    }
    if (
      f.closestRampDepth !== null &&
      prevRampDepth.current !== null &&
      prevRampDepth.current > 0.5 &&
      f.closestRampDepth < 0
    ) {
      const spawnDist = depthToMetres(f.closestRampDepth);
      const ttr = timeToReach(f.closestRampDepth, f.depthSpeed);
      const ts = new Date();
      const label = `${ts.getSeconds().toString().padStart(2,'0')}.${Math.floor(ts.getMilliseconds()/10).toString().padStart(2,'0')} RAMP spawned @ ${spawnDist.toFixed(0)}m — ${ttr.toFixed(1)}s to reach`;
      setSpawnEvents(prev => {
        const next = [label, ...prev];
        return next.length > 12 ? next.slice(0, 12) : next;
      });
    }

    prevObsDepth.current = f.closestObstacleDepth;
    prevRampDepth.current = f.closestRampDepth;
  }, []);

  useEffect(() => {
    debugListeners.push(onFrame);
    return () => {
      const idx = debugListeners.indexOf(onFrame);
      if (idx !== -1) debugListeners.splice(idx, 1);
    };
  }, [onFrame]);

  const obsDepth = frame?.closestObstacleDepth ?? null;
  const rampDepth = frame?.closestRampDepth ?? null;
  const dps = frame?.depthSpeed ?? 0;

  // Visible distance = spawn point (negative depth → behind horizon)
  const spawnVisibleDist = frame ? depthToMetres(frame.spawnMinDepth) : 0;
  const spawnMaxDist = frame ? depthToMetres(frame.spawnMaxDepth) : 0;

  const obsTTR = obsDepth !== null ? timeToReach(obsDepth, dps) : null;
  const rampTTR = rampDepth !== null ? timeToReach(rampDepth, dps) : null;

  // Colour coding for urgency
  const urgencyColor = (ttr: number | null) => {
    if (ttr === null) return '#556677';
    if (ttr < 1.5) return '#ff4d6d';
    if (ttr < 3.0) return '#ffd60a';
    return '#00e5ff';
  };

  return (
    <>
      {/* Toggle button — sits above the LOG button */}
      <button
        onClick={onToggle}
        className="pointer-events-auto absolute z-30"
        style={{
          bottom: '230px',
          left: '12px',
          background: 'rgba(13,13,15,0.88)',
          border: '1px solid rgba(255,214,10,0.35)',
          borderRadius: '10px',
          padding: '6px 10px',
          color: '#ffd60a',
          fontSize: '10px',
          fontFamily: 'monospace',
          letterSpacing: '0.08em',
          backdropFilter: 'blur(8px)',
        }}
        aria-label="Toggle spawn debug overlay"
      >
        {visible ? '✕ DBG' : '📡 DBG'}
      </button>

      {visible && (
        <div
          className="pointer-events-none absolute z-30"
          style={{
            bottom: '270px',
            left: '12px',
            width: '270px',
            background: 'rgba(8,10,14,0.94)',
            border: '1px solid rgba(255,214,10,0.22)',
            borderRadius: '12px',
            backdropFilter: 'blur(14px)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{
            padding: '6px 10px',
            borderBottom: '1px solid rgba(255,214,10,0.12)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ color: '#ffd60a', fontSize: '9px', fontFamily: 'monospace', letterSpacing: '0.12em' }}>
              SPAWN DEBUG
            </span>
            <span style={{ color: '#556677', fontSize: '9px', fontFamily: 'monospace' }}>
              t={frame ? frame.runTime.toFixed(1) : '--'}s
            </span>
          </div>

          {/* Live stats grid */}
          <div style={{ padding: '8px 10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
            {/* Depth speed */}
            <StatRow label="DEPTH SPEED" value={dps > 0 ? dps.toFixed(4) + '/s' : '--'} color="#00e5ff" />
            <StatRow label="DIFFICULTY" value={frame ? (frame.postSafeFrac * 100).toFixed(0) + '%' : '--'} color="#a8dadc" />

            {/* Spawn range */}
            <StatRow
              label="SPAWN DIST MIN"
              value={frame ? spawnVisibleDist.toFixed(0) + 'm' : '--'}
              color="#f4a261"
            />
            <StatRow
              label="SPAWN DIST MAX"
              value={frame ? spawnMaxDist.toFixed(0) + 'm' : '--'}
              color="#f4a261"
            />

            {/* Active counts */}
            <StatRow label="ACTIVE OBS" value={frame ? String(frame.activeObstacles) : '--'} color="#ffd60a" />
            <StatRow label="ACTIVE RAMPS" value={frame ? String(frame.activeRamps) : '--'} color="#ffd60a" />
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', margin: '0 10px' }} />

          {/* Closest obstacle */}
          <div style={{ padding: '6px 10px' }}>
            <div style={{ color: '#556677', fontSize: '8px', fontFamily: 'monospace', marginBottom: '4px', letterSpacing: '0.1em' }}>
              CLOSEST APPROACHING
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
              <StatRow
                label="OBS depth"
                value={obsDepth !== null ? obsDepth.toFixed(3) : 'none'}
                color={urgencyColor(obsTTR)}
              />
              <StatRow
                label="OBS dist"
                value={obsDepth !== null ? depthToMetres(obsDepth).toFixed(0) + 'm' : '--'}
                color={urgencyColor(obsTTR)}
              />
              <StatRow
                label="OBS time"
                value={obsTTR !== null && isFinite(obsTTR) ? obsTTR.toFixed(2) + 's' : '--'}
                color={urgencyColor(obsTTR)}
              />
              <StatRow
                label="RAMP time"
                value={rampTTR !== null && isFinite(rampTTR) ? rampTTR.toFixed(2) + 's' : '--'}
                color={urgencyColor(rampTTR)}
              />
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', margin: '0 10px' }} />

          {/* Spawn event log */}
          <div style={{ padding: '6px 10px 8px' }}>
            <div style={{ color: '#556677', fontSize: '8px', fontFamily: 'monospace', marginBottom: '4px', letterSpacing: '0.1em' }}>
              SPAWN EVENTS
            </div>
            {spawnEvents.length === 0 && (
              <p style={{ color: '#334455', fontSize: '8px', fontFamily: 'monospace' }}>Waiting for spawns…</p>
            )}
            {spawnEvents.map((ev, i) => (
              <div key={i} style={{ color: i === 0 ? '#ffd60a' : '#445566', fontSize: '8px', fontFamily: 'monospace', lineHeight: '1.5' }}>
                {ev}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function StatRow({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
      <span style={{ color: '#334455', fontSize: '7px', fontFamily: 'monospace', letterSpacing: '0.08em' }}>{label}</span>
      <span style={{ color, fontSize: '11px', fontFamily: 'monospace', fontWeight: 700 }}>{value}</span>
    </div>
  );
}
